import { useCallback, useEffect, useRef } from "react";

/**
 * Schedule a callback after `delayMs` of quiet. Each new `schedule` call
 * resets the timer. `flush` runs the pending call immediately; `cancel`
 * drops it.
 */
export function useDebouncedCallback<Args extends unknown[]>(
	callback: (...args: Args) => void,
	delayMs: number,
): {
	cancel: () => void;
	flush: () => void;
	schedule: (...args: Args) => void;
} {
	const callbackRef = useRef(callback);
	useEffect(() => {
		callbackRef.current = callback;
	});

	const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
	const argsRef = useRef<Args | null>(null);

	const cancel = useCallback(() => {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		argsRef.current = null;
	}, []);

	const flush = useCallback(() => {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		const pendingArgs = argsRef.current;
		argsRef.current = null;
		if (pendingArgs) {
			callbackRef.current(...pendingArgs);
		}
	}, []);

	const schedule = useCallback(
		(...args: Args) => {
			argsRef.current = args;
			if (timeoutRef.current !== null) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(() => {
				timeoutRef.current = null;
				const pendingArgs = argsRef.current;
				argsRef.current = null;
				if (pendingArgs) {
					callbackRef.current(...pendingArgs);
				}
			}, delayMs);
		},
		[delayMs],
	);

	useEffect(() => cancel, [cancel]);

	return { cancel, flush, schedule };
}
